/**
 * DownloadAppBanner (iter-109)
 *
 * Sticky top strip on the Dashboard that nudges mobile-web visitors to install
 * the native app. Silent no-op when:
 *   • Running inside Capacitor (iOS/Android native shell) — they're already
 *     in the app.
 *   • Viewport is desktop-sized (width > 640) — we only target phones.
 *   • Neither store URL is configured OR banner is disabled by the
 *     super-admin (Admin → App Links).
 *   • User has dismissed it in the last 7 days.
 *
 * Backend contract:
 *   GET /api/config/app-links → { ios_url, android_url, banner_enabled }
 */
import { useEffect, useState } from 'react';
import { X, Smartphone, Download } from 'lucide-react';
import { api } from '@/lib/api';

const DISMISS_KEY = 'gm_dl_banner_dismissed_at';
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

// Detect the phone OS from userAgent. Returns 'ios' | 'android' | null.
function detectPhoneOS() {
  if (typeof navigator === 'undefined') return null;
  const ua = navigator.userAgent || '';
  // iOS: iPhone / iPad / iPod. Modern iPadOS masquerades as Mac — detect via
  // touch points as a secondary signal.
  if (/iPhone|iPod/i.test(ua)) return 'ios';
  if (/iPad/i.test(ua)) return 'ios';
  if (/Macintosh/i.test(ua) && (navigator.maxTouchPoints || 0) > 1) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return null;
}

function isNativeCapacitor() {
  try {
    const cap = require('@capacitor/core');
    return !!(cap && cap.Capacitor && cap.Capacitor.isNativePlatform && cap.Capacitor.isNativePlatform());
  } catch {
    return false;
  }
}

function isRecentlyDismissed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (!ts) return false;
    return Date.now() - ts < DISMISS_TTL_MS;
  } catch {
    return false;
  }
}

export default function DownloadAppBanner() {
  const [config, setConfig] = useState(null); // {ios_url, android_url, banner_enabled}
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Bail out fast when the banner can't apply — no fetch, no re-render.
    if (isNativeCapacitor()) return;
    if (isRecentlyDismissed()) { setDismissed(true); return; }
    // Desktop viewport: skip entirely so we don't advertise a phone app
    // to a laptop user.
    if (typeof window !== 'undefined' && window.innerWidth > 640) return;
    // The endpoint is public — anonymous visitors on Landing can hit it too
    // (though we currently only mount the banner on authenticated Dashboard).
    api.get('/config/app-links')
      .then((r) => setConfig(r.data || null))
      .catch(() => setConfig(null));
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    setDismissed(true);
  };

  if (dismissed || !config || !config.banner_enabled) return null;

  const os = detectPhoneOS();
  const storeUrl = os === 'ios' ? config.ios_url : os === 'android' ? config.android_url : '';
  if (!storeUrl) return null;

  const label = os === 'ios' ? 'App Store' : 'Google Play';

  return (
    <div
      className="flex items-center gap-3 p-3 rounded-2xl border-2 border-[#7E5BEF]/30 bg-[#EDE4FF] mb-4"
      data-testid="download-app-banner"
      role="region"
      aria-label="Get the BecAim mobile app"
    >
      <div className="w-10 h-10 rounded-xl bg-[#7E5BEF] text-white flex items-center justify-center shrink-0">
        <Smartphone size={20} strokeWidth={3} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-extrabold text-[#3B2A70] leading-tight">
          Get the BecAim app
        </p>
        <p className="text-[11px] font-bold text-[#5B4A9A] leading-snug">
          Faster, offline-ready, and gets push nudges.
        </p>
      </div>
      <a
        href={storeUrl}
        target="_blank"
        rel="noreferrer noopener"
        className="gm-btn gm-btn-primary py-2 px-3 text-xs whitespace-nowrap"
        data-testid="download-app-cta"
      >
        <Download size={14} strokeWidth={3} className="mr-1" />
        {label}
      </a>
      <button
        onClick={dismiss}
        className="p-1 text-[#7E5BEF] hover:bg-[#DDD0FF] rounded-lg shrink-0"
        aria-label="Dismiss"
        data-testid="download-app-dismiss"
      >
        <X size={18} strokeWidth={3} />
      </button>
    </div>
  );
}
